/**
 * Fetch ContentComponent data for the CMS dictionary.
 *
 * @note modifying the data here will affect multiple pages, override data in the page if needed
 * @todo remove after migration to CMS pages
 */

import { gql } from '@apollo/client';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import {
  FRAGMENT_SHORT_TEXT_FIELD,
  FRAGMENT_IMAGE_FIELD,
  FRAGMENT_CONTENT_FIELD,
} from '$apollo/gql/cms/contentful';
import type { CmsContentfulRichText } from '$cms/contentful.types';
import { CmsContentfulTypename } from '$cms/contentful.types';
import { getRichTextByKey, getShortTextByKey } from '$cms/getDataByKey';
import type { CmsContentDictionary } from '$cms/index';
import type { HeroSectionTranslatedEntities } from './schema';
import { HeroSectionSchema } from './schema';

/**
 * @todo add Number fragment
 */
export const QUERY_HERO_SECTION_CONTENT_COMPONENT = gql`
  query heroSectionContentComponent($preview: Boolean, $locale: String!) {
    heroSectionContentComponent: contentComponent(
      id: "zQELhiQEVT233kGbo4K8Q"
      locale: $locale
      preview: $preview
    ) {
      entriesCollection: translatedEntitiesCollection {
        items {
          ...ShortTextField
          ...ContentField
          ...ImgField
        }
      }
    }
  }

  ${FRAGMENT_SHORT_TEXT_FIELD}
  ${FRAGMENT_CONTENT_FIELD}
  ${FRAGMENT_IMAGE_FIELD}
`;

const headingFallback: Document = {
  data: {},
  content: [
    {
      data: {},
      content: [
        {
          data: {},
          marks: [],
          value: 'Car maintenance,',
          nodeType: 'text',
        },
      ],
      nodeType: BLOCKS.PARAGRAPH,
    },
    {
      data: {},
      content: [
        {
          data: {},
          marks: [],
          value: 'made easy',
          nodeType: 'text',
        },
      ],
      nodeType: BLOCKS.PARAGRAPH,
    },
  ],
  nodeType: BLOCKS.DOCUMENT,
};

export const getHeroSectionCmsData = (
  cmsData: CmsContentDictionary
): { readonly translatedEntities: HeroSectionTranslatedEntities } => {
  const getShortText = getShortTextByKey.bind(cmsData);
  const getRichText = getRichTextByKey.bind(cmsData);
  // const getImage = getImageByKey.bind(cmsData);

  const getShortTextField = (key: string, fallback: string) => ({
    key,
    value: getShortText(key, fallback),
  });

  const getRichTextField = (key: string, fallback: Document): CmsContentfulRichText =>
    getRichText(key) || {
      key,
      __typename: CmsContentfulTypename.richText,
      content: {
        json: fallback,
      },
    };

  // const getImageField = (key: string, fallback: Parameters<typeof getImage>[1]) => {
  //   const imgData = getImage(key, fallback);
  //   return {
  //     key,
  //     url: imgData.img.original_secure_url,
  //     width: imgData.img.width,
  //     height: imgData.img.height,
  //     alt: imgData.alt,
  //     title: imgData.title ?? undefined, // gql return `null` when not defined
  //   };
  // };

  const translatedEntities = HeroSectionSchema.translatedEntities.parse({
    heading: getRichTextField('VrmHero.heading.home', headingFallback),
    postcodeLabel: getShortTextField('VrmHero.postcodeLabel.home', 'Your postcode'),
    vrmLabel: getShortTextField('VrmHero.vrmLabel.home', 'Your registration number'),
    mainCta: getShortTextField('VrmHero.mainCta.home', 'Get an Instant Quote'),
    price: getShortTextField('VrmHero.price.vidange', ''),
    invalidPostcodeMessage: getShortTextField(
      'VrmHero.invalidPostcodeMessage.home',
      'Hmmm, invalid postcode. Please double check and try again.'
    ),
    invalidVRMMessage: getShortTextField(
      'VrmHero.invalidVRMMessage.home',
      'Hmmm, invalid reg number. Please double check and try again.'
    ),
    missingPostcodeMessage: getShortTextField(
      'VrmHero.missingPostcodeMessage.home',
      'Please enter your postcode'
    ),
    missingVRMMessage: getShortTextField(
      'VrmHero.missingVRMMessage.home',
      'Please enter your registration number'
    ),
    askExpertsText: getShortTextField('VrmHero.askExpertsText.home', 'Not sure what’s wrong with your car?'),
    askExpertsLinkText: getShortTextField('VrmHero.askExpertsLinkText.home', 'Speak to our in-house experts'),
    askExpertsAnchorId: getShortTextField('VrmHero.askExpertsAnchorId.home', 'need-help'),
    speechBubbleOne: getShortTextField('VrmHero.speechBubbleOne.home', 'We collect'),
    speechBubbleTwo: getShortTextField('VrmHero.speechBubbleTwo.home', 'We fix'),
    speechBubbleThree: getShortTextField('VrmHero.speechBubbleThree.home', 'And we return your car'),
  });

  return {
    translatedEntities,
  };
};
